// Shared shapes for the upload flow. UploadModal, audioProcessingService, Menu
// and the Files page all describe "a file the officer is about to use in a
// report" with these, so they cannot drift apart.

export type MediaType = "audio" | "image" | "video" | "document";

export interface Speaker {
  name: string;
  role: string;
}

export type AudioType = "narrative" | "interaction";

// What a recording saved to Files can be marked as.
export type SaveAudioType = AudioType | "other";

export type FileSource = "case" | "form_submission" | "library";

export interface FileConfig {
  file: File;
  // Name to save under (Files library). The extension is kept from `file`.
  displayName?: string;
  // Files library only: save the audio as "Other", with no transcription type
  // and nothing transcribed automatically.
  libraryOther?: boolean;
  mediaType: MediaType;
  audioType: AudioType;
  speakers: Speaker[];
  languages: string[];
  additionalContext: string;
  keepVideo?: boolean;
  // Footage tier: read the video itself, not just its audio.
  videoAnalysis?: { mode: "verify" | "enrich"; analyzeAll: boolean };
  axonSource?: {
    evidenceId: string;
    title: string;
    size: number;
    duration: number;
    contentType: string;
  };
  // A file that already exists in the library (a past case upload, a form-fill
  // upload, or a file saved straight to Files). `file` is an empty placeholder.
  previousUpload?: {
    caseFileId: string;
    source?: FileSource;
    fileUrl: string;
    transcriptionUrl?: string;
    originalAudioType: AudioType;
    fileName: string;
    fileSize: string;
    // Set for non-audio library files.
    kind?: FileKind;
  };
}

export interface UploadOptions {
  includeTimestamps: boolean;
  detailLevel: "detailed" | "standard" | "concise";
  reportTemplateId?: string | null;
  callSign?: string | null;
  // Investigation linked for narrative context (investigations add-on only)
  investigationId?: string | null;
}

export type FileKind = "audio" | "video" | "image" | "document" | "spreadsheet";

// Determine media type from a browser File.
export const getMediaTypeFromFile = (file: File): MediaType => {
  const mimeType = file.type.toLowerCase();
  if (mimeType.startsWith("audio/")) return "audio";
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType.startsWith("video/")) return "video";
  // Document types: PDF, Word, Excel, etc.
  if (
    mimeType.startsWith("application/pdf") ||
    mimeType.startsWith("application/msword") ||
    mimeType.startsWith("application/vnd.") ||
    mimeType.startsWith("text/")
  ) {
    return "document";
  }
  // Fallback based on extension
  const ext = file.name.split(".").pop()?.toLowerCase();
  if (["mp3", "wav", "m4a", "ogg", "flac", "aac"].includes(ext || ""))
    return "audio";
  if (["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"].includes(ext || ""))
    return "image";
  if (["mp4", "mov", "avi", "mkv", "webm"].includes(ext || "")) return "video";
  return "document";
};

// The shape of one row from GET /api/files that a FileConfig is built from.
export interface LibraryItemLike {
  id: string;
  source: FileSource;
  kind: FileKind;
  fileName: string;
  fileSize: string;
  fileType: string;
  fileUrl: string;
  metadata: {
    audioType?: AudioType;
    transcriptionUrl?: string;
    context?: string;
    speakers?: Speaker[];
  };
}

// Turn a library row into a pre-filled FileConfig. This is the mapping the
// "previous upload" picker has always used, now shared with the Files page so
// both behave identically (including the instant no-copy reuse when the audio
// type is left unchanged).
export function fileConfigFromLibraryItem(item: LibraryItemLike): FileConfig {
  // A previous upload whose original video is still in storage (the file URL
  // is the video, not the extracted audio) can have its footage analyzed like
  // a fresh upload; every other audio-ish file is audio.
  const isVideoStillStored = Boolean(
    item.fileType?.startsWith("video/") &&
      /\.(mp4|mov|webm|m4v)(\?|$)/i.test(item.fileUrl || ""),
  );
  const mediaType: MediaType =
    item.kind === "image"
      ? "image"
      : item.kind === "document" || item.kind === "spreadsheet"
        ? "document"
        : isVideoStillStored
          ? "video"
          : "audio";
  const audioType: AudioType = item.metadata.audioType || "narrative";

  return {
    file: new File([], item.fileName || "previous-upload", {
      type: item.fileType,
    }),
    mediaType,
    audioType,
    speakers: item.metadata.speakers || [],
    languages: [],
    additionalContext: item.metadata.context || "",
    previousUpload: {
      caseFileId: item.id,
      source: item.source,
      fileUrl: item.fileUrl,
      transcriptionUrl: item.metadata.transcriptionUrl,
      originalAudioType: audioType,
      fileName: item.fileName,
      fileSize: item.fileSize,
      kind: item.kind,
    },
  };
}

// ---- Non-audio files (PDFs, images, Word/text, spreadsheets) -----------------
// Mirrors server/src/services/files/documentFiles.ts. The server re-checks both
// the extension and the size, so these only give the officer an early answer.

export const AUDIO_ACCEPT = ".m4a,.mp3,.wav,.mp4,audio/*,video/mp4";
export const DOCUMENT_ACCEPT = ".pdf,.docx,.doc,.txt,.jpg,.jpeg,.png,.webp,.xlsx,.xls,.csv,.tsv";

const DOC_KIND_BY_EXT: Record<string, FileKind> = {
  pdf: "document",
  docx: "document",
  doc: "document",
  txt: "document",
  jpg: "image",
  jpeg: "image",
  png: "image",
  webp: "image",
  xlsx: "spreadsheet",
  xls: "spreadsheet",
  csv: "spreadsheet",
  tsv: "spreadsheet",
};

export const DOCUMENT_MAX_MB: Record<"document" | "image" | "spreadsheet", number> = {
  document: 100,
  image: 15,
  spreadsheet: 20,
};

// The kind of a supported non-audio file, or null when it is not one we read.
export function documentKindOf(fileName: string): "document" | "image" | "spreadsheet" | null {
  const ext = (fileName.split(".").pop() || "").toLowerCase();
  const kind = DOC_KIND_BY_EXT[ext];
  return kind === "document" || kind === "image" || kind === "spreadsheet" ? kind : null;
}

// Default name for a recording saved to Files, e.g. "recording-2026-10-02-1552"
// (dash case, 24-hour time, no commas or colons). Built from the computer's own
// local date and time, so it matches the officer's clock and time zone wherever
// they are.
export const defaultRecordingName = (now: Date = new Date()) => {
  const p = (n: number) => String(n).padStart(2, "0");
  return `recording-${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}-${p(now.getHours())}${p(now.getMinutes())}`;
};
